import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { MarkdownView } from "../lib/markdown";
import { useSeo, faqSchema, blogPostSchema } from "../lib/useSeo";
import { FAQS } from "../lib/constants";
import { LEGAL, LegalPage } from "./LegalPages";
import NotFoundPage from "./NotFoundPage";
import { BlogNewsletter } from "../components/BlogNewsletter";
import { RelatedBlogPosts } from "../components/RelatedBlogPosts";
import { BlogReferralBadge } from "../components/BlogReferralBadge";
import { Mail, Phone, Youtube, Linkedin, Instagram, Facebook, Rss } from "lucide-react";

const TRUST = "AI Shorthand commands are simple prompting shortcuts designed to help users tell AI what they want it to do. They are not official built-in slash commands supplied by ChatGPT, Claude or Gemini.";

const PAGES = {
  contact: { title: "Contact", body: [
    "Questions about your membership, billing or the dictionary? Email us at info@virtualautochat.uk and we'll get back to you within one working day.",
  ]},
  accessibility: { title: "Accessibility", body: [
    "AI Shorthand is designed mobile-first with large touch targets, high-contrast text and simple navigation. We aim to meet WCAG 2.1 AA standards.",
    "If you experience any accessibility issue, please contact support and we will prioritise a fix.",
  ]},
};

export function InfoPage() {
  const { slug } = useParams();
  const legal = LEGAL[slug];
  const page = PAGES[slug];
  useSeo(!legal && page ? { title: page.title, description: page.body[0].slice(0, 155) } : {});
  if (legal) return <LegalPage slug={slug} />;
  if (!page) return <NotFoundPage />;
  return (
    <div className="max-w-2xl mx-auto px-4 py-16 min-h-[60vh]">
      <h1 className="font-display text-3xl font-bold tracking-tight">{page.title}</h1>
      <div className="mt-6 space-y-4">
        {page.body.map((p, i) => <p key={i} className="text-slate-600 leading-relaxed">{p}</p>)}
      </div>
      {slug === "contact" && <ContactDetails />}
    </div>
  );
}

function ContactDetails() {
  return (
    <div className="mt-8 space-y-8">
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="contact-details-card">
        <a href="mailto:info@virtualautochat.uk" className="flex items-center gap-3 text-slate-700 hover:text-[#B8860B] transition-colors" data-testid="contact-email-link">
          <span className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0"><Mail size={18} className="text-[#B8860B]" /></span>
          <span><span className="block text-xs uppercase tracking-wider text-slate-400 font-semibold">Email</span><span className="font-semibold">info@virtualautochat.uk</span></span>
        </a>
        <a href="tel:+442030514338" className="flex items-center gap-3 text-slate-700 hover:text-[#B8860B] transition-colors" data-testid="contact-phone-link">
          <span className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0"><Phone size={18} className="text-[#B8860B]" /></span>
          <span><span className="block text-xs uppercase tracking-wider text-slate-400 font-semibold">Phone</span><span className="font-semibold">0203 051 4338</span></span>
        </a>
      </div>
      <div>
        <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">Follow Us</h2>
        <div className="flex flex-wrap gap-3 mt-4">
          <a href="https://www.youtube.com/@AIPromptShortcutCommands" target="_blank" rel="noopener noreferrer"
            data-testid="contact-youtube"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:border-red-500/60 hover:bg-red-50 rounded-full px-4 py-2 transition-colors">
            <Youtube size={16} className="text-red-500" /> YouTube
          </a>
          <a href="https://www.linkedin.com/showcase/aipromptshortcuts" target="_blank" rel="noopener noreferrer"
            data-testid="contact-linkedin"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:border-sky-500/60 hover:bg-sky-50 rounded-full px-4 py-2 transition-colors">
            <Linkedin size={16} className="text-sky-500" /> LinkedIn
          </a>
          <a href="https://www.instagram.com/aipromptshortcuts/" target="_blank" rel="noopener noreferrer"
            data-testid="contact-instagram"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:border-pink-500/60 hover:bg-pink-50 rounded-full px-4 py-2 transition-colors">
            <Instagram size={16} className="text-pink-500" /> Instagram
          </a>
          <a href="https://www.facebook.com/aipromptshortcuts" target="_blank" rel="noopener noreferrer"
            data-testid="contact-facebook"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:border-blue-500/60 hover:bg-blue-50 rounded-full px-4 py-2 transition-colors">
            <Facebook size={16} className="text-blue-500" /> Facebook
          </a>
          <a href="https://www.tiktok.com/@xpressprompts" target="_blank" rel="noopener noreferrer"
            data-testid="contact-tiktok"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:border-fuchsia-500/60 hover:bg-fuchsia-50 rounded-full px-4 py-2 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="text-fuchsia-500">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.57a8.16 8.16 0 0 0 4.77 1.52V7.63a4.85 4.85 0 0 1-1.84-.94z"/>
            </svg>
            TikTok
          </a>
          <a href="https://www.reddit.com/r/XpressPrompts/" target="_blank" rel="noopener noreferrer"
            data-testid="contact-reddit"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:border-orange-500/60 hover:bg-orange-50 rounded-full px-4 py-2 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="text-orange-500">
              <path d="M22 12.14a2.14 2.14 0 0 0-3.63-1.55 10.53 10.53 0 0 0-5.71-1.81l1-4.61 3.21.68a1.52 1.52 0 1 0 .15-.73l-3.57-.76a.37.37 0 0 0-.44.28l-1.09 5.13a10.55 10.55 0 0 0-5.79 1.82A2.14 2.14 0 1 0 3.28 14a4.14 4.14 0 0 0-.05.65C3.23 17.51 6.85 20 11.31 20s8.08-2.49 8.08-5.35a4.14 4.14 0 0 0-.05-.63A2.14 2.14 0 0 0 22 12.14zM8.14 13.6a1.32 1.32 0 1 1 1.32 1.32 1.32 1.32 0 0 1-1.32-1.32zm6.89 3.45a4.79 4.79 0 0 1-3.11.94h0a4.79 4.79 0 0 1-3.1-.94.36.36 0 1 1 .47-.55 4.09 4.09 0 0 0 2.63.78h0a4.09 4.09 0 0 0 2.63-.78.36.36 0 0 1 .47.55zm-.11-2.13a1.32 1.32 0 1 1 1.32-1.32 1.32 1.32 0 0 1-1.32 1.32z"/>
            </svg>
            Reddit
          </a>
        </div>
      </div>
    </div>
  );
}

export function FaqPage() {
  useSeo({
    title: "FAQ — Frequently Asked Questions",
    description: "Do I need to know prompt engineering? Does AI Shorthand work with ChatGPT, Claude and Gemini? Answers to common questions.",
    jsonLd: faqSchema(FAQS),
  });
  return (
    <div className="max-w-2xl mx-auto px-4 py-16 min-h-[60vh]">
      <h1 className="font-display text-3xl font-bold tracking-tight">FAQ</h1>
      <div className="mt-8 space-y-6">
        {FAQS.map((f, i) => (
          <div key={i}>
            <h2 className="font-semibold text-slate-900">{f.q}</h2>
            <p className="text-slate-600 mt-1.5 text-sm leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
      <p className="mt-10 text-sm text-slate-500 bg-slate-50 rounded-xl p-4">{TRUST}</p>
    </div>
  );
}

export function Blog() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);
  const [muted, setMuted] = useState(true);
  const [activeTag, setActiveTag] = useState("All");
  useSeo({ title: "XpressPrompts Blogs", description: "Practical guides on getting more from ChatGPT, Claude and Gemini with AI Shorthand commands." });
  useEffect(() => {
    const load = (retry) => api.get("/blog").then((r) => { setPosts(r.data); setError(false); })
      .catch(() => { if (retry) setTimeout(() => load(false), 1500); else setError(true); });
    load(true);
  }, []);

  // Build unique, ordered tag list from loaded posts
  const allTags = (() => {
    const seen = new Set();
    const out = [];
    (posts || []).forEach((p) => (p.tags || []).forEach((t) => {
      if (!seen.has(t)) { seen.add(t); out.push(t); }
    }));
    return out;
  })();

  const filtered = (posts || []).filter((p) =>
    activeTag === "All" || (p.tags || []).includes(activeTag)
  );
  const featured = filtered.find((p) => p.featured) || filtered[0];
  const secondary = filtered.filter((p) => p !== featured).slice(0, 2);
  const rest = filtered.filter((p) => p !== featured && !secondary.includes(p));

  const dateLabel = (p) => (p.published_at || p.created_at || "").slice(0, 10);
  const firstTag = (p) => (p.tags || [])[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-16 min-h-[60vh]">
      <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37] shadow-xl shadow-amber-900/10 mb-10 aspect-video bg-black cursor-pointer"
        onClick={() => setMuted((m) => !m)} data-testid="blog-intro-video-frame">
        <video src="/videos/blog-intro.mp4" autoPlay muted={muted} loop playsInline
          className="w-full h-full object-cover" data-testid="blog-intro-video" />
        {muted && (
          <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm">
            Tap for sound
          </span>
        )}
      </div>

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">AI Shorthand</p>
          <h1 data-testid="blog-page-title" className="font-display text-4xl sm:text-5xl font-extrabold tracking-normal leading-tight mt-1.5 break-words">XpressPrompts Blogs</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">Launches, guides and candid behind-the-scenes writing on getting real work out of ChatGPT, Claude and Gemini.</p>
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/rss.xml`} target="_blank" rel="noopener noreferrer"
          data-testid="blog-rss-link" title="Subscribe via RSS"
          className="inline-flex items-center gap-2 text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-300 hover:bg-orange-100 rounded-full px-3 py-1.5 transition-colors">
          <Rss size={14} /> RSS
        </a>
      </div>

      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-6" data-testid="blog-tag-chips">
          <button
            data-testid="blog-tag-all"
            onClick={() => setActiveTag("All")}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
              activeTag === "All" ? "bg-[#0B0F0D] text-[#D4AF37]" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
            }`}>
            All ({posts?.length || 0})
          </button>
          {allTags.map((t) => {
            const n = (posts || []).filter((p) => (p.tags || []).includes(t)).length;
            return (
              <button key={t}
                data-testid={`blog-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setActiveTag(t)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                  activeTag === t ? "bg-[#0B0F0D] text-[#D4AF37]" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                }`}>
                {t} ({n})
              </button>
            );
          })}
        </div>
      )}

      {error && <p className="text-slate-500 mt-8" data-testid="blog-load-error">Couldn't load posts — please refresh the page to try again.</p>}
      {posts && filtered.length === 0 && !error && (
        <p className="text-slate-400 mt-12 text-center">Nothing under "{activeTag}" yet.</p>
      )}

      {/* ---- Magazine lead: huge asymmetric featured card ---- */}
      {featured && (
        <Link to={`/blog/${featured.slug}`} data-testid="blog-featured-card"
          className="group block mt-10 bg-gradient-to-br from-[#0B0F0D] via-[#111611] to-[#0B0F0D] text-white rounded-3xl overflow-hidden ring-1 ring-[#D4AF37]/30 hover:ring-[#D4AF37]/80 transition-all shadow-2xl shadow-black/20">
          <div className="grid md:grid-cols-5">
            {featured.image ? (
              <div className="md:col-span-3 relative overflow-hidden">
                <img src={featured.image} alt={featured.title} loading="lazy"
                  className="w-full h-64 md:h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0B0F0D]/70 md:to-[#0B0F0D]" />
                <span className="absolute top-5 left-5 bg-[#D4AF37] text-[#0B0F0D] text-[11px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md">★ Featured</span>
              </div>
            ) : (
              <div className="md:col-span-3 h-64 md:h-full bg-[#0B0F0D] flex items-center justify-center relative">
                <span className="font-display text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-[#D4AF37] to-amber-500">/&gt;</span>
                <span className="absolute top-5 left-5 bg-[#D4AF37] text-[#0B0F0D] text-[11px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md">★ Featured</span>
              </div>
            )}
            <div className="md:col-span-2 p-7 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-2 flex-wrap">
                {firstTag(featured) && (
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-2.5 py-0.5">
                    {firstTag(featured)}
                  </span>
                )}
                <span className="text-[11px] text-white/50 font-semibold uppercase tracking-wider">{dateLabel(featured)}</span>
              </div>
              <h2 className="font-display font-extrabold text-2xl md:text-4xl mt-3 leading-tight">{featured.title}</h2>
              <p className="text-sm md:text-base text-white/70 mt-4 leading-relaxed line-clamp-4">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-1.5 mt-6 text-[#D4AF37] font-bold text-sm group-hover:translate-x-1 transition-transform">
                Read the full post →
              </span>
            </div>
          </div>
        </Link>
      )}

      {/* ---- Secondary row: 2 medium cards ---- */}
      {secondary.length > 0 && (
        <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="blog-secondary">
          {secondary.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-post-link-${p.slug}`}
              className="group flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#D4AF37]/60 hover:shadow-xl hover:-translate-y-0.5 transition-all">
              {p.image ? (
                <div className="relative overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" data-testid={`blog-thumb-${p.slug}`}
                    className="w-full h-56 object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                  {firstTag(p) && (
                    <span className="absolute top-3 left-3 bg-black/70 backdrop-blur text-[#D4AF37] text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
                      {firstTag(p)}
                    </span>
                  )}
                </div>
              ) : (
                <div data-testid={`blog-thumb-fallback-${p.slug}`}
                  className="w-full h-56 bg-[#0B0F0D] flex flex-col items-center justify-center gap-2 relative">
                  <span className="font-display text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-[#D4AF37] to-amber-500">/&gt;</span>
                  {firstTag(p) && (
                    <span className="absolute top-3 left-3 bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
                      {firstTag(p)}
                    </span>
                  )}
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">{dateLabel(p)}</p>
                <h3 className="font-display font-bold text-xl mt-1.5 leading-snug group-hover:text-emerald-800 transition-colors">{p.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                <span className="inline-block mt-4 text-emerald-700 text-sm font-semibold group-hover:translate-x-1 transition-transform">Read more →</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* ---- Dense grid for the rest ---- */}
      {rest.length > 0 && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-list">
          {rest.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-post-link-${p.slug}`}
              className="group flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#D4AF37]/60 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              {p.image ? (
                <div className="relative overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" data-testid={`blog-thumb-${p.slug}`}
                    className="w-full h-44 object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                  {firstTag(p) && (
                    <span className="absolute top-3 left-3 bg-black/70 backdrop-blur text-[#D4AF37] text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
                      {firstTag(p)}
                    </span>
                  )}
                </div>
              ) : (
                <div data-testid={`blog-thumb-fallback-${p.slug}`}
                  className="w-full h-44 bg-[#0B0F0D] flex flex-col items-center justify-center gap-2 relative">
                  <span className="font-display text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-[#D4AF37] to-amber-500">/&gt;</span>
                  {firstTag(p) && (
                    <span className="absolute top-3 left-3 bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
                      {firstTag(p)}
                    </span>
                  )}
                </div>
              )}
              <div className="p-5 flex-1 flex flex-col">
                <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">{dateLabel(p)}</p>
                <h3 className="font-display font-bold text-base mt-1 leading-snug group-hover:text-emerald-800 transition-colors">{p.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed line-clamp-2 flex-1">{p.excerpt}</p>
                <span className="inline-block mt-3 text-emerald-700 text-xs font-semibold group-hover:translate-x-1 transition-transform">Read more →</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <BlogNewsletter />
      <Link to="/pricing" data-testid="blog-trial-offer-link" className="block mt-14">
        <img src="/assets/free-trial-offer.gif" alt="Free Trial Offer — Try it for 24hrs Free. Talk to AI in Shorthand."
          data-testid="blog-trial-offer-gif" className="w-full rounded-2xl shadow-lg hover:scale-[1.01] transition-transform" />
      </Link>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useSeo(post ? {
    title: post.seo_title || post.title,
    description: post.seo_description || post.excerpt,
    type: "article",
    jsonLd: blogPostSchema(post),
  } : {});
  useEffect(() => {
    let active = true;
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => { if (active) setPost(r.data); }).catch(() => { if (active) setNotFound(true); });
    window.scrollTo(0, 0);
    return () => { active = false; };
  }, [slug]);
  if (notFound) return <div className="p-20 text-center text-slate-500">Post not found. <Link to="/blog" className="text-emerald-700 font-semibold">Back to blog</Link></div>;
  if (!post) return <div className="p-20 text-center text-slate-400">Loading…</div>;
  return (
    <div className="max-w-6xl mx-auto px-4 py-16 min-h-[60vh]" data-testid="blog-post-page">
      <article className="max-w-2xl mx-auto">
      <Link to="/blog" data-testid="blog-post-back" className="text-sm text-emerald-700 font-semibold hover:underline">← All posts</Link>
      <h1 data-testid="blog-post-title" className="font-display text-3xl sm:text-4xl font-extrabold tracking-normal mt-4 leading-tight break-words">{post.title}</h1>
      <p data-testid="blog-post-date" className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-3">{(post.published_at || "").slice(0, 10)}</p>
      <div className="mt-8"><MarkdownView content={post.content} /></div>
      <Link to="/pricing" data-testid="blog-post-cta-banner" className="block mt-12">
        <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ai-shorthand/artifacts/1g8p56qk_1%20Act%20now%20get%20XpressPrompts%20no%20credit%20card%20needed.webp"
             alt="Get FREE instant access to XpressPrompts for 24 hours — no credit card needed"
             loading="lazy"
             className="w-full rounded-2xl shadow-lg hover:scale-[1.01] transition-transform" />
      </Link>
      </article>
      <RelatedBlogPosts slug={slug} />
      <BlogNewsletter key={slug} />
      <BlogReferralBadge />
    </div>
  );
}
